import {
  type Margin,
  Box,
  Button,
  ChevronIcon,
  CrossIcon,
  Divider,
  FullscreenPanel,
  IconButton,
  Lead,
  MarginProvider,
  Paper,
  Slot,
  Text,
  Trail,
  Typography,
  UserRoundFillIcon,
} from '@yandex-delivery/blocks'
import type {Translator} from '@yandex-delivery/core/i18n'
import {useI18n} from '@yandex-delivery/core/i18n'
import clsx from 'clsx'
import {type ReactElement, memo, useState} from 'react'
import {useController, useFormContext} from 'react-hook-form'

import {PhoneInput} from '../../../components/Input/PhoneInput'
import {ErrorIcon} from '../ErrorIcon/ErrorIcon'
import {MapModal} from '../MapModal/MapModal'
import type {PickupPoint, PickupTarget} from '../types'
import {createNames, renderPickupPointName} from '../utils'
import * as classes from './PointModal.classes'

type Props = {
  name: string
  target: PickupTarget
  onClose: VoidFunction
  onContinue: VoidFunction
}
type State = {
  opened: boolean
  key: string
}

const margin: Margin = [16, 16, 16, 16]

const getPhoneLabel = (phone: string, isDestination: boolean, i18n: Translator) => {
  if (!phone) {
    return isDestination
      ? i18n('bdui.pickup_points.point-modal.recipient-phone-choose', 'Телефон получателя *')
      : i18n('bdui.pickup_points.point-modal.sender-phone-choose', 'Телефон отправителя *')
  }

  return isDestination
    ? i18n('bdui.pickup_points.point-modal.recipient-phone', 'Телефон получателей')
    : i18n('bdui.pickup_points.point-modal.sender-phone', 'Телефон отправителя')
}

export const PointModal = memo<Props>(function PointModal(props: Props): ReactElement {
  const {name, target, onClose, onContinue} = props
  const names = createNames(name)
  const isDestination = target === 'destination'

  const {control, trigger, watch, setValue, getFieldState, formState} = useFormContext()
  const pickupPoint = watch(names.pickupPoint)
  const i18n = useI18n()
  const {field: phoneField, fieldState: phoneFieldState} = useController({control, name: names.phone})

  const [state, setState] = useState<State>({
    opened: false,
    key: name,
  })

  function openHandler() {
    setState({
      opened: true,
      // key нужен из-за специфики работы компонента модалки (она не демонтируется после закрытия)
      // если в краце, то для сброса стейта внутри компонента
      // ----
      // напрямую передать в компоненте key={pickupPoint?._id} нельзя,
      // потому что не сработает анимация закртия модалки, из за мгновенного обновления key
      key: pickupPoint?._id || state.key,
    })
  }

  function closeHandler() {
    setState({
      key: state.key,
      opened: false,
    })
  }

  function changeHandler(nextPickupPoint: PickupPoint) {
    setValue(names.pickupPoint, nextPickupPoint)
    closeHandler()
  }

  return (
    <>
      <Paper item="header">
        <Slot size="l">
          <Text
            // @ts-expect-error Property 'Component' does not exist on type...
            Component={Typography}
            thickness="medium"
            variant="title4"
          >
            {isDestination ? i18n('bdui.address.input.to', 'Куда') : i18n('bdui.address.input.from', 'Откуда')}
          </Text>
          <Trail mr="xs">
            <IconButton onPress={onClose} shape="circle" size="m" variant="ghost">
              <CrossIcon />
            </IconButton>
          </Trail>
        </Slot>
      </Paper>
      <Paper item="footer">
        <Slot className={classes.slot} onClick={openHandler} py="xs" size="l">
          <Lead>
            <Box
              className={clsx(classes.icon, pickupPoint && classes.iconType[pickupPoint._type])}
              justifyContent="center"
            />
          </Lead>
          <Text>
            {pickupPoint ? (
              renderPickupPointName(pickupPoint, i18n('bdui.pickup_points.point-slot.isMarket', 'Пункт Яндекс Маркета'))
            ) : (
              <Typography color="minor" variant="body2">
                {i18n('bdui.pickup_points.point-modal.point-select', 'Выберите пункт *')}
              </Typography>
            )}
          </Text>
          {getFieldState(names.pickupPoint, formState).invalid && (
            <Trail>
              <ErrorIcon />
            </Trail>
          )}
          <Trail mr="s">
            <ChevronIcon />
          </Trail>
        </Slot>
        <Slot className={classes.slot} onClick={undefined} size="l">
          <Lead>
            <UserRoundFillIcon />
          </Lead>
          <Text
            // @ts-expect-error Property 'Component' does not exist on type...
            alignItems="stretch"
            color="minor"
            Component={Typography}
            variant="body2"
          >
            <PhoneInput
              className={classes.phoneInput}
              label={getPhoneLabel(phoneField.value, isDestination, i18n)}
              name={names.phone}
              onBlur={() => trigger(names.phone)}
              placeholder=""
              variant="line"
            />
            <Divider isInvalid={phoneFieldState.invalid} />
            {phoneFieldState.invalid && (
              <Typography color="error" variant="caption2">
                {phoneFieldState.error?.message}
              </Typography>
            )}
          </Text>
        </Slot>
      </Paper>
      <Paper float="bottom" p="s">
        <Button
          onPress={async () => {
            if (await trigger(name)) {
              onContinue()
            }
          }}
          variant="action"
          fullWidth
        >
          {i18n('bdui.pickup_points.button.continue', 'Продолжить')}
        </Button>
      </Paper>
      <FullscreenPanel anchor="right" opened={state.opened}>
        <MarginProvider offset={margin}>
          <MapModal onChange={changeHandler} onClose={closeHandler} pickupPoint={pickupPoint} target={target} />
        </MarginProvider>
      </FullscreenPanel>
    </>
  )
})
